import { envelopeData } from '@/lib/envelope'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { resolveEntityQueryOptions } from '@/lib/queries/lesson'
import { courseDetailQueryOptions } from '@/lib/queries/catalog'
import { findValidCertificateForProgram, studentCertificatesQueryOptions } from '@/lib/queries/certificates'
import { VideoPlayer } from '@/components/watch/video-player'
import { LessonInfoBar } from '@/components/watch/lesson-info-bar'
import { LessonContent } from '@/components/watch/lesson-content'
import { InstructorSection } from '@/components/watch/instructor-section'
import { PurchaseCTA } from '@/components/shared/purchase-cta'
import { CertificateCTA } from '@/components/shared/certificate-cta'
import { NotFoundView } from '@/components/shared/not-found-view'
import { WatchPageSkeleton } from '@/components/shared/loading-skeleton'

export const Route = createFileRoute('/_app/$menu/$programa/')({
  component: ProgramPage,
  pendingComponent: () => <WatchPageSkeleton />,
  errorComponent: () => (
    <div className="text-center py-16">
      <p className="text-muted-foreground">Erro ao carregar programa. Tente recarregar a página.</p>
    </div>
  ),
})

function ProgramPage() {
  const { menu, programa: program } = Route.useParams()

  // Antes de tudo, e com `useQuery` em vez de `useSuspenseQuery`, por dois
  // motivos: (1) os hooks abaixo ficam depois de `return`s condicionais, então
  // um hook novo no meio mudaria a contagem entre renders; (2) certificado é
  // acessório — se a rota falhar ou demorar, a página do programa não pode
  // sumir junto (o `errorComponent` da rota engoliria a tela inteira).
  const { data: certificatesData } = useQuery(studentCertificatesQueryOptions())

  const { data: resolveData } = useSuspenseQuery(resolveEntityQueryOptions(menu, program))
  const entity = envelopeData(resolveData)

  if (!entity) return <NotFoundView />

  const { data: courseData } = useSuspenseQuery(courseDetailQueryOptions(entity.entityId))
  const course = envelopeData(courseData)

  if (!course) return <WatchPageSkeleton />

  const hasAccess = entity.hasAccess !== false && course.hasAccess !== false
  const isLocked = !hasAccess

  // O certificado é um fato datado: quem concluiu continua vendo o CTA mesmo se
  // o acesso ao conteúdo tiver expirado depois.
  const certificate = findValidCertificateForProgram(certificatesData, entity.entityId)

  return (
    <div className="space-y-4">
      {course.videoUrl ? (
        <VideoPlayer
          src={course.videoUrl}
          lessonId={entity.entityId}
          startAt={0}
        />
      ) : course.thumbnailUrl ? (
        <div className="relative aspect-video bg-muted rounded-lg overflow-hidden">
          <img src={course.thumbnailUrl} alt={course.name} className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className="aspect-video bg-muted rounded-lg flex items-center justify-center">
          <p className="text-muted-foreground">Sem vídeo de apresentação</p>
        </div>
      )}

      <div>
        <h1 className="text-xl lg:text-2xl font-bold text-foreground">{course.name}</h1>
        {course.description && (
          <p className="text-sm text-muted-foreground mt-1">{course.description}</p>
        )}
      </div>

      <LessonInfoBar
        duration={course.totalLessons ? `${course.totalLessons} episódios` : undefined}
      />

      {certificate && (
        <CertificateCTA code={certificate.code} issuedAt={certificate.issuedAt} />
      )}

      {isLocked ? (
        <PurchaseCTA title={`Adquira "${course.name}"`} price={course.price} />
      ) : (
        course.content && <LessonContent html={course.content} />
      )}

      {course.instructorName && (
        <InstructorSection
          name={course.instructorName}
          bio={course.instructorBio}
          avatarUrl={course.instructorPhoto}
        />
      )}
    </div>
  )
}
